import { useApolloClient } from "@apollo/client";
import { useFormContext } from "contexts/FormContext";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import Select from "react-select";
import pricelistgql from "gql/dashboard/sales/priceList.gql";
import Signature from "./Signature";
import useAxios from "hooks/useAxios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppContext } from "contexts/AppContext";
import useAlert from "hooks/useAlert";

const Checkout = ({ show, onHide }) => {
  // contexts
  const { appState, setAppState } = useAppContext();
  const { formState, setFormState } = useFormContext();

  // states
  const [validated, setValidated] = useState(false);
  const [emailOptions, setEmailOptions] = useState([]);
  // hooks
  const ref = useRef(null);
  const { loading, data, error, post, put } = useAxios();
  const navigate = useNavigate();
  const apollo = useApolloClient();
  const alert = useAlert();
  const [searchParams] = useSearchParams();

  // functions
  const loadCustomerBranches = (customer) => {
    setFormState({ key: "info.branches", value: customer.branches });
    if (customer.branches?.length) {
      setFormState({
        key: "info.customerBranch",
        value: {
          value: customer.branches[0],
          label: customer.branches[0].name,
        },
      });
    } else {
      setFormState({
        key: "info.customerBranch",
        value: null,
      });
    }
  };

  const loadPriceList = (customer) => {
    setFormState({
      key: "info.priceList",
      value: null,
    });

    apollo
      .query({
        query: pricelistgql,
        variables: { id: customer.id },
      })
      .then((data) => {
        const priceList = data.data.companies[0].priceList;
        setFormState({
          key: "info.priceList",
          value: priceList,
        });
      })
      .catch((err) => { });
  };

  const setCustomerEmail = (customer) => {
    setFormState({
      key: "info.customerEmail",
      value: customer.email1 || "",
    });
  };

  const onOutbox = () => {
    const data = {
      id: formState.info.id,
      salesMan: formState.info.salesMan,
      docDate: formState.info.salesDate,
      deliveryDate: formState.info.deliveryDate,
      customer: formState.info.customer?.value,
      customerBranch: formState.info.customerBranch?.value,
      customerEmail: formState.info.customerEmail,
      customerOrderNumber: formState.info.customerPONumber,
      paymentTerms: formState.info.paymentTerms?.value,
      deliveryPoint: formState.info.customerBranch?.value.address,
      terms: formState.info.terms,
      labeling: formState.info.labeling,
      remarks: formState.info.remarks,
      draft: formState.info.draft,
      customerSignature: formState.info.customerSignature,
      salesManSignature: formState.info.salesmanSignature,
      details: formState.details
        .filter((x) => x.item?.id)
        .map((cartItem) => ({
          id: cartItem.id,
          deliveryDate: formState.info.deliveryDate,
          item: cartItem.item.id,
          quantity: cartItem.qty,
          deliveryPoint: formState.info.customerBranch?.value.address,
          salesPrice: cartItem.price || 0,
          extra: !!cartItem.extra,
        })),
    };

    const sales = JSON.parse(localStorage.getItem("sales") || "[]");
    if (formState.info.dId) {
      data.dId = formState.info.dId;
      data.docNumber = `Temp-SO-${data.dId}`;
      localStorage.setItem(
        "sales",
        JSON.stringify([
          ...sales.filter((x) => x.dId != formState.info.dId),
          data,
        ])
      );
    } else {
      const nextId = parseInt(localStorage.getItem("sales_id") || 0) + 1;
      data.dId = nextId;
      data.docNumber = `Temp-SO-${data.dId}`;
      localStorage.setItem("sales", JSON.stringify([...sales, data]));
      localStorage.setItem("sales_id", nextId);
      setFormState({ key: "info.dId", value: data.dId });
    }
    localStorage.removeItem("temp-order");
    navigate(`/dashboard/sales/orders/outbox?open=${data.dId}`);
  };

  const onDraft = (online = true) => {
    navigate("/dashboard/sales/orders/draft");
    return;
    // setFormState({
    //   key: "info.draft",
    //   value: true,
    // });

    // if (online) onSubmit(true);
    // else onOutbox();
  };

  const continueSubmit = (draft, email = false) => {
    const data = {
      id: formState.info.id,
      sales_man: formState.info.salesMan.id,
      doc_date: formState.update ? formState.info.salesDate : new Date().toISOString().split("T")[0],
      deliveryDate: formState.info.deliveryDate,
      customer: formState.info.customer.value.id,
      customer_branch: formState.info.customerBranch?.value.id,
      customer_email: formState.info.customerEmail,
      email,
      customerOrderNumber: formState.info.customerPONumber,
      paymentTerms: formState.info.paymentTerms.value.id,
      deliveryPoint: formState.info.customerBranch?.value.address,
      terms: formState.info.terms,
      labeling: formState.info.labeling,
      remarks: formState.info.remarks,
      customer_signature: formState.info.customerSignature,
      sales_man_signature: formState.info.salesmanSignature,
      draft,
      c_id: searchParams.get("c-id"),
      details: formState.details
        .filter((x) => x.item?.id)
        .map((cartItem) => ({
          id: cartItem.id,
          deliveryDate: formState.info.deliveryDate,
          item: cartItem.item.id,
          quantity: cartItem.qty,
          deliveryPoint: formState.info.customerBranch?.value.address,
          sales_price: cartItem.price || 0,
          extra: !!cartItem.extra,
        })),
    };

    if (!formState.update) {
      post("/sales/salesorders/", data);
    } else {
      put(`/sales/salesorders/${data.id}/`, data);
    }
  };

  const onSubmit = (draft = false) => {
    if (ref.current.checkValidity() === false) {
      setValidated(true);
      return;
    }
    setValidated(false);

    if (formState.info.customerEmail) {
      alert
        .fire({
          title: `Email The Document`,
          html: `Do you want to email this sales order to <b>${formState.info.customer.value.name}</b>?<br /><br /><span class="text-muted">Email: ${formState.info.customerEmail}</span>`,
          icon: "question",
          showDenyButton: true,
          confirmButtonText: "Yes",
          denyButtonText: "No",
        })
        .then((confirmed) => {
          continueSubmit(draft, confirmed.isConfirmed);
        });
    } else {
      continueSubmit(draft);
    }
  };

  // effects
  useEffect(() => {
    if (formState.info.customer) {
      loadCustomerBranches(formState.info.customer.value);
      loadPriceList(formState.info.customer.value);
      if (!formState.info.customerEmail) {
        setCustomerEmail(formState.info.customer.value);
      }
      if (!formState.info.paymentTerms) {
        const pt = appState.resources.paymentTerms.find(
          (x) =>
            x.id ===
            formState.info.customer.value.companyFinancials?.paymentTerms?.id
        );
        if (pt) {
          setFormState({
            key: "info.paymentTerms",
            value: {
              label: pt.name,
              value: pt,
            },
          });
        } else {
          setFormState({
            key: "info.paymentTerms",
            value: null,
          });
        }
      }
    }
  }, [formState.info.customer]);

  useEffect(() => {
    if (!loading && data) {
      toast.success(
        `Document was ${formState.update ? "updated" : "created"} successfully.`
      );
      if (formState.info.dId) {
        const sales = JSON.parse(localStorage.getItem("sales") || "[]");
        localStorage.setItem(
          "sales",
          JSON.stringify(sales.filter((x) => x.dId != formState.info.dId))
        );
      }
      localStorage.removeItem("temp-order");
      localStorage.removeItem("last-order");
      setAppState({
        key: "lastOrder",
        value: null,
      });
      navigate(`/dashboard/sales/orders?open=${data.data.data.id}`);
    } else if (!loading && error) {
      toast.info("Order saved in outbox.");
      onOutbox();
    }
  }, [loading]);

  useEffect(() => {
    if (formState.info.customer) {
      const email1 = formState.info?.customer?.value?.email1;
      const email2 = formState.info?.customer?.value?.email2;
      const email3 = formState.info?.customer?.value?.email3;
      const customerEmail = formState.info?.customerEmail;
      const options = [];
      if (email1) {
        options.push({ value: email1, label: email1 });
      }
      if (email2) {
        options.push({ value: email2, label: email2 });
      }
      if (email3) {
        options.push({ value: email3, label: email3 });
      }
      if (customerEmail && !options.some((x) => x.value === customerEmail)) {

        options.push({ value: customerEmail, label: customerEmail });
      }
      setEmailOptions(options);
    }
  }, [formState.info.customer]);

  return (
    <React.Fragment>
      <Modal show={show} onHide={onHide} fullscreen>
        <Modal.Header closeButton>
          <Modal.Title>Checkout</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form noValidate validated={validated} ref={ref}>
            <Row>
              <Col md={9}>
                <Row>
                  <h1>Sales Information</h1>
                </Row>
                {/* <Row className="mb-4">
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Sales Man</Form.Label>
                      <Form.Control
                        value={formState.info.salesMan.name}
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        defaultValue={new Date().toISOString().split("T")[0]}
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row> */}
                <Row className="mb-4">
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Customer</Form.Label>
                      <Select
                        required
                        value={formState.info.customer}
                        onChange={(e) => {
                          console.log(e);
                          setFormState({
                            batch: [
                              { key: "info.customer", value: e },
                              { key: "info.customerEmail", value: e?.value?.eamil1 },
                            ],
                          });
                        }}
                        options={formState.customers?.map((x) => ({
                          value: x,
                          label: x.name,
                        }))}
                      />
                      {validated && !formState.info.customer && (
                        <Form.Text className="text-danger">
                          This field is required
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Customer Branch</Form.Label>
                      <Select
                        required
                        value={formState.info.customerBranch}
                        onChange={(e) => {
                          setFormState({
                            key: "info.customerBranch",
                            value: e,
                          });
                        }}
                        options={formState.info.branches?.map((x) => ({
                          value: x,
                          label: x.name,
                        }))}
                      />
                      <Form.Text className="text-success fw-bold">
                        {formState.info.customerBranch?.value?.address}
                      </Form.Text>
                      {validated && !formState.info.customerBranch && (
                        <Form.Text className="text-danger">
                          This field is required
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col sm={6}>
                    <Form.Group>
                      {/* <Form.Label>Sales Date</Form.Label>
                      <Form.Control
                        type="date"
                        required
                        value={formState.info.salesDate}
                        onChange={(e) =>
                          setFormState({
                            key: "info.salesDate",
                            value: e.target.value,
                          })
                        }
                      /> */}
                      <Form.Label>Customer PO Number</Form.Label>
                      <Form.Control
                        value={formState.info.customerPONumber}
                        onChange={(e) =>
                          setFormState({
                            key: "info.customerPONumber",
                            value: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Delivery Date</Form.Label>
                      <Form.Control
                        type="date"
                        required
                        value={formState.info.deliveryDate}
                        onChange={(e) =>
                          setFormState({
                            key: "info.deliveryDate",
                            value: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {/* <Row className="mb-4">
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Customer Email</Form.Label>
                      <Form.Control
                        id="email"
                        value={formState.info.customerEmail}
                        placeholder="example@erp.com"
                        onChange={(e) =>
                          setFormState({
                            key: "info.customerEmail",
                            value: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Customer PO Number</Form.Label>
                      <Form.Control
                        value={formState.info.customerPONumber}
                        onChange={(e) =>
                          setFormState({
                            key: "info.customerPONumber",
                            value: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row> */}
                {/* <Row>
                  <h1>Finilize Order</h1>
                </Row> */}
                <Row className="mb-4">
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Customer Email</Form.Label>
                      {/* <Form.Control
                        id="email"
                        type="email"
                        value={formState.info.customerEmail}
                        placeholder="customer@mgherp.com"
                        onChange={(e) =>
                          setFormState({
                            key: "info.customerEmail",
                            value: e.target.value,
                          })
                        }
                      /> */}
                      <Select
                        required
                        value={{ value: formState.info.customerEmail, label: formState.info.customerEmail }}
                        onChange={(e) =>
                          setFormState({
                            key: "info.customerEmail",
                            value: e.value,
                          })
                        }
                        options={emailOptions}
                        onBlur={(e) => {
                          if (!e.target.value) return;
                          e.preventDefault();
                          e.stopPropagation();
                          const newOptions = [...(emailOptions?.filter((x) => x.value !== e.target.value))];
                          setEmailOptions([...newOptions, { value: e.target.value, label: e.target.value }]);
                          setFormState({
                            key: "info.customerEmail",
                            value: e.target.value,
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            const newOptions = [...(emailOptions?.filter((x) => x.value !== e.target.value))];
                            setEmailOptions([...newOptions, { value: e.target.value, label: e.target.value }]);
                            setFormState({
                              key: "info.customerEmail",
                              value: e.target.value,
                            });
                          }
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={12} md={6}>
                    <Form.Group>
                      <Form.Label>Payment Terms</Form.Label>
                      <Select
                        required
                        value={formState.info.paymentTerms}
                        onChange={(e) =>
                          setFormState({ key: "info.paymentTerms", value: e })
                        }
                        options={formState.paymentTerms?.map((x) => ({
                          value: x,
                          label: x.name,
                        }))}
                      />
                      {validated && !formState.info.paymentTerms && (
                        <Form.Text className="text-danger">
                          This field is required
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  {/* <Col sm={12} md={6} className="my-3">
                    <Form.Group>
                      <Form.Label>Terms And Conditions</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={formState.info.terms}
                        onChange={(e) =>
                          setFormState({
                            key: "info.terms",
                            value: e.target.value,
                          })
                        }
                      ></Form.Control>
                    </Form.Group>
                  </Col>
                  <Col sm={12} md={6} className="my-3">
                    <Form.Group>
                      <Form.Label>
                        Packaging And Labeling Requirements
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={formState.info.labeling}
                        onChange={(e) =>
                          setFormState({
                            key: "info.labeling",
                            value: e.target.value,
                          })
                        }
                      ></Form.Control>
                    </Form.Group>
                  </Col> */}
                </Row>
                <Row>
                  <Col sm={12} className="my-3">
                    <Form.Group>
                      <Form.Label>Remarks</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={formState.info.remarks}
                        onChange={(e) =>
                          setFormState({
                            key: "info.remarks",
                            value: e.target.value,
                          })
                        }
                      ></Form.Control>
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              <Col md={3} className="border-start">
                <Row>
                  <h1>Signatures</h1>
                </Row>
                <Row className="mb-4 w-100">
                  <Col sm={12}>
                    <Form.Label>Customer Signature</Form.Label>
                    <Signature
                      value={formState.info.customerSignature}
                      setValue={(val) =>
                        setFormState({
                          key: "info.customerSignature",
                          value: val,
                        })
                      }
                      buttonText="Sign"
                      header="Customer Signature"
                    />
                  </Col>
                  <Col sm={12}>
                    <Form.Label>Salesman Signature</Form.Label>
                    <Signature
                      value={formState.info.salesmanSignature}
                      setValue={(val) =>
                        setFormState({
                          key: "info.salesmanSignature",
                          value: val,
                        })
                      }
                      buttonText="Sign"
                      header="Salesman Signature"
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {/* <Dropdown variant="warning" drop="up">
            <Dropdown.Toggle variant="warning" disabled={loading}>
              {!loading && "Draft Order"}
              {loading && <Spinner />}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => onDraft()}>
                Online Draft
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onDraft(false)}>
                Offline Draft
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown> */}
          <Button
            disabled={loading}
            variant="warning"
            onClick={() => onDraft()}
          >
            {!loading && "Save to draft"}
            {loading && <Spinner />}
          </Button>
          <Button
            disabled={loading}
            variant="success"
            onClick={() => onSubmit()}
          >
            {!loading && "Place Order"}
            {loading && <Spinner />}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default Checkout;
